// FeedbackButton.jsx
import { useState } from 'react';
import {
  Button,
  Modal,
  Box,
  Typography,
  TextField,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';

const ACCENT = 'rgb(219, 234, 254)';
const ACCENT_BORDER = '#60A5FA';

export default function FeedbackButton({
  apiBase = process.env.REACT_APP_API_URL,
  page = '',
}) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const close = () => {
    if (sending) return;
    setOpen(false);
    setSent(false);
    setError('');
  };

  const handleSend = async () => {
    if (!feedback.trim()) return;

    setSending(true);
    setError('');

    try {
      const res = await fetch(`${apiBase}/api/sendfeedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedback: feedback.trim(),
          page
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setSent(true);
      setFeedback('');
    } catch (err) {
      console.error('Feedback failed:', err);
      setError('Failed to send feedback.');
    } finally {
      setSending(false);
      close();
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color='error'
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none' }}
      >
        Feedback
      </Button>

      <Modal open={open} onClose={close}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 420,
            maxWidth: '95vw',
            bgcolor: 'white',
            borderRadius: 3,
            border: `1px solid ${ACCENT_BORDER}`,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              bgcolor: ACCENT,
              borderBottom: `1px solid ${ACCENT_BORDER}`,
            }}
          >
            <Typography sx={{ fontWeight: 800 }}>Send feedback</Typography>
            <Typography variant="body2" color="text.secondary">
              Report a problem or suggest an improvement.
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              {sent && (
                <Alert severity="success">
                  Feedback sent.
                </Alert>
              )}

              {error && (
                <Alert severity="error">
                  {error}
                </Alert>
              )}

              <TextField
                label="Describe the issue"
                multiline
                minRows={4}
                fullWidth
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="What happened? What should be improved?"
              />

              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button onClick={close} disabled={sending}>
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={sending || !feedback.trim()}
                >
                  {sending ? <CircularProgress size={20} /> : 'Send'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </>
  );
}